import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { addToPrefsList } from "@/lib/prefsList";
import { money } from "@/lib/format";
import { Warning, X, CalendarBlank, Info } from "@phosphor-icons/react";

/**
 * ZEN-784 (Maryse, 17 Oct 2026): a reminder that belongs to one beneficiary
 * shows only on that beneficiary's Overview; "All beneficiaries" shows
 * everyone's. `studentId` is the beneficiary picked at the top, or nothing for
 * All. The server answers for that beneficiary only.
 */
function useNudges(studentId) {
  return useQuery({
    queryKey: ["compliance-nudges", studentId || "all"],
    queryFn: async () => (await api.get("/dashboard/compliance-nudges", { params: studentId ? { student_id: studentId } : {} })).data,
    staleTime: 5 * 60 * 1000,
  });
}

const byName = (a, b) => (a.student_name || "").localeCompare(b.student_name || "");

/** Rows grouped by beneficiary, beneficiaries in name order so a close never reshuffles them. */
function byBeneficiary(rows) {
  const groups = new Map();
  for (const r of rows) {
    if (!groups.has(r.student_id)) groups.set(r.student_id, []);
    groups.get(r.student_id).push(r);
  }
  return [...groups.entries()].map(([sid, items]) => ({ sid, items, student_name: items[0].student_name })).sort(byName);
}

/**
 * Closing is remembered per beneficiary: "<prefix>_<beneficiary id>". A key
 * with no beneficiary in it was written before ZEN-784 and still closes the
 * reminder for everyone, so nothing a family already closed comes back.
 */
export function useCloseByBeneficiary(prefix) {
  const qc = useQueryClient();
  const { data: prefs } = useQuery({ queryKey: ["user-prefs"], queryFn: async () => (await api.get("/user-prefs")).data });
  const closedKeys = new Set(prefs?.dismissed_panels || []);
  const isClosed = (sid) => closedKeys.has(prefix) || closedKeys.has(`${prefix}_${sid}`);
  const close = (sid) => addToPrefsList(qc, "dismissed_panels", `${prefix}_${sid}`);
  return { isClosed, close, ready: !!prefs };
}

/**
 * 1a. December Deadline Guardrail Banner (Dashboard, Nov 1 – Dec 31)
 * Shows when current tax year has eligible expenses not yet matched by withdrawals.
 * @param {Date} [nowOverride] — injectable date for testing
 */
export function DecemberDeadlineBanner({ nowOverride, studentId }) {
  const now = nowOverride || new Date();
  const month = now.getMonth() + 1; // 1-indexed
  const year = now.getFullYear();
  const { isClosed, close, ready } = useCloseByBeneficiary(`dec_deadline_${year}`);
  const { data: nudges } = useNudges(studentId);

  // ZEN-784 (Maryse, 17 Oct 2026): one reminder per beneficiary, each with its
  // own figure and its own close; never one message naming several. The
  // server alone decides who is listed (any figure above zero before
  // rounding), so the screen does not filter on the rounded figure again.
  const open = (nudges?.dec_deadline_by_student || []).filter((g) => !isClosed(g.student_id)).sort(byName);
  // Only show Nov–Dec
  if (!ready || month < 11 || !open.length) return null;

  return (
    <>
      {open.map((g) => (
        <div key={g.student_id} className="mb-4 flex items-start gap-2.5 rounded-lg border border-[#D97706]/30 bg-[#FFFBEB] px-4 py-3" data-testid="dec-deadline-banner" data-student={g.student_id}>
          <CalendarBlank size={18} weight="bold" className="mt-0.5 shrink-0 text-[#D97706]" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#92400E]">December deadline{g.student_name ? ` — ${g.student_name}` : ""}</p>
            <p className="mt-0.5 text-sm text-[#92400E]">
              You have <b className="tnum">{money(g.gap, "USD")}</b> of qualified expenses in {year} not yet matched to 529 withdrawals.
              Withdrawals must happen by December 31 to count for this year&apos;s expenses.
            </p>
            <Link to="/withdrawals" className="mt-1 inline-block text-xs font-semibold text-[#D97706] hover:underline">
              Go to 529 Distributions
            </Link>
          </div>
          <button onClick={() => close(g.student_id)} className="shrink-0 rounded p-0.5 text-[#D97706] hover:bg-[#D97706]/10" data-testid={`dec-deadline-dismiss-${g.student_id}`} aria-label="Dismiss">
            <X size={14} weight="bold" />
          </button>
        </div>
      ))}
    </>
  );
}

/**
 * 1b. December Withdrawal Inline Note (Withdrawal form)
 * Shows when the withdrawal date is in December.
 * @param {string} dateRequested — the date_requested value (YYYY-MM-DD)
 */
export function DecemberWithdrawalNote({ dateRequested }) {
  if (!dateRequested) return null;
  const month = parseInt(dateRequested.slice(5, 7), 10);
  if (month !== 12) return null;

  return (
    <div className="col-span-2 flex items-start gap-2 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-3 py-2 text-[11px] leading-relaxed text-[#92400E]" data-testid="dec-withdrawal-note">
      <Info size={14} weight="fill" className="mt-0.5 shrink-0 text-[#D97706]" />
      <span>
        Heads up: money taken out in December cannot cover expenses you will pay in January — a withdrawal is never justified by a cost paid in a later year. For spring-term costs paid in January, withdraw in January instead.
      </span>
    </div>
  );
}

/**
 * 2. Payee Guidance on Withdrawals (Withdrawal form)
 * Shows when actual_recipient is "owner".
 * @param {string} actualRecipient — the actual_recipient value
 */
export function OwnerRecipientNote({ actualRecipient }) {
  if (actualRecipient !== "owner") return null;

  return (
    <div className="col-span-2 flex items-start gap-2 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-3 py-2 text-[11px] leading-relaxed text-[#92400E]" data-testid="owner-recipient-note">
      <Info size={14} weight="fill" className="mt-0.5 shrink-0 text-[#D97706]" />
      <span>
        Distributions paid to you (the account owner) are reported under <b>your</b> Social Security number on the 1099-Q,
        while the school&apos;s 1098-T is under your beneficiary&apos;s — a mismatch that commonly triggers automated IRS letters.
        Distributions paid to the student or directly to the school are reported under the student&apos;s SSN and attract less scrutiny.
        Either way is legal — this only affects paperwork friction.
      </span>
    </div>
  );
}

/**
 * 3. Fall COA Snapshot Reminder (Dashboard, Sep 1 – Oct 31)
 * Shows when active schools have terms overlapping the new academic year without supporting docs.
 * @param {Date} [nowOverride] — injectable date for testing
 */
export function CoaSnapshotReminder({ nowOverride, studentId }) {
  const now = nowOverride || new Date();
  const month = now.getMonth() + 1; // 1-indexed
  const year = now.getFullYear();
  const { isClosed, close, ready } = useCloseByBeneficiary(`coa_snapshot_${year}`);
  const { data: nudges } = useNudges(studentId);

  // ZEN-784 (Maryse, 17 Oct 2026): one reminder per beneficiary, each closed on
  // its own. ZEN-781: terms ticked "Enter later" are listed whatever the month
  // and cannot be dismissed — they clear themselves once the amount is entered.
  const season = ready && month >= 9 && month <= 10;
  const schools = season ? (nudges?.schools_needing_coa_snapshot || []).filter((s) => !isClosed(s.student_id)) : [];
  const waiting = (nudges?.terms_entered_later || []).map((w, i) => ({ ...w, _n: i }));
  const children = byBeneficiary([...schools.map((s) => ({ ...s, _kind: "snapshot" })), ...waiting.map((w) => ({ ...w, _kind: "later" }))]);
  if (!children.length) return null;

  return (
    <div data-testid="coa-snapshot-reminder">
      {children.map(({ sid, items }) => {
        const mine = items.filter((x) => x._kind === "snapshot");
        const later = items.filter((x) => x._kind === "later");
        return (
          <div key={sid} className="mb-4 flex items-start gap-2.5 rounded-lg border border-[#D97706]/30 bg-[#FFFBEB] px-4 py-3" data-testid="coa-snapshot-child" data-student={sid}>
            <Warning size={18} weight="bold" className="mt-0.5 shrink-0 text-[#D97706]" />
            <div className="min-w-0 flex-1">
              {mine.length > 0 && (<>
              <p className="text-sm font-semibold text-[#92400E]">New school year — grab this year&apos;s Cost of Attendance page</p>
              <p className="mt-0.5 text-sm text-[#92400E]">
                Screenshot your school&apos;s published COA (financial-aid site) and attach it to the term as your audit evidence.
              </p>
              <div className="mt-1 space-y-0.5">
                {mine.map((s, i) => (
                  <Link key={i} to="/students" className="block text-xs font-semibold text-[#D97706] hover:underline">
                    {s.student_name} — {s.school_name}
                  </Link>
                ))}
              </div>
              </>)}
              {later.length > 0 && (
                <div className={`${mine.length ? "mt-2" : ""} space-y-0.5`} data-testid="coa-enter-later-reminder">
                  {later.map((w) => (
                    <Link key={w._n} to="/students" className="block text-sm font-semibold text-[#92400E] hover:underline" data-testid={`coa-enter-later-${w._n}`}>
                      {w.school_name}{w.year_label ? ` ${w.year_label}` : ""}: enter the cost of attendance and its supporting document.
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {mine.length > 0 && (
            <button onClick={() => close(sid)} className="shrink-0 rounded p-0.5 text-[#D97706] hover:bg-[#D97706]/10" data-testid={`coa-snapshot-dismiss-${sid}`} aria-label="Dismiss">
              <X size={14} weight="bold" />
            </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
